import Link from "next/link";
import { Button } from "@/components/ui/button";
import { HardHat, ArrowLeft } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const dynamic = "force-static";

export const metadata = {
  title: "FAQ — RateRight",
  description:
    "Frequently asked questions about RateRight, the simplest way to hire construction workers in Australia.",
};

const faqs = [
  {
    question: "What is RateRight?",
    answer:
      "RateRight is an AI-powered construction hiring marketplace. Contractors post jobs and get matched with skilled workers nearby — all in under two minutes.",
  },
  {
    question: "How much does it cost?",
    answer:
      "It's free to sign up and post jobs. You only pay a $50 flat fee when you confirm a hire. No subscriptions, no percentages, no hidden fees. Workers never pay anything.",
  },
  {
    question: "Is RateRight free for workers?",
    answer:
      "Yes — always. Signing up, building your profile, getting matched, and getting hired are all completely free for workers.",
  },
  {
    question: "How does AI matching work?",
    answer:
      "When a contractor posts a job, our AI ranks available workers by distance, relevant experience, trade skills, and ratings. The best matches appear first so contractors can hire with confidence.",
  },
  {
    question: "How long does it take to get hired?",
    answer:
      "Most workers get matched within minutes of a job being posted. Contractors can have someone on site as early as the next day.",
  },
  {
    question: "What trades does RateRight support?",
    answer:
      "RateRight supports all construction trades — labourers, carpenters, electricians, plumbers, concreters, painters, and more. If it's construction, we cover it.",
  },
  {
    question: "Do I need a White Card?",
    answer:
      "Workers are asked to verify their White Card (General Construction Induction Card) during signup. This helps contractors hire with confidence, but you can still create a profile without one.",
  },
  {
    question: "Where is RateRight available?",
    answer:
      "RateRight is currently live in Sydney, Australia. We're expanding to other cities soon.",
  },
  {
    question: "How do I sign up as a contractor?",
    answer:
      "Tap 'I'm Hiring' on the home page, enter your company name or ABN, and our AI will build your company profile automatically. You can post your first job straight away.",
  },
  {
    question: "How do I sign up as a worker?",
    answer:
      "Tap 'I'm Looking For Work' on the home page and answer five quick questions. Our AI builds your full profile in about two minutes. No CV needed.",
  },
  {
    question: "How do payments work?",
    answer:
      "When a contractor confirms a hire, they pay the $50 flat fee securely via Stripe. Payment is only charged at the point of hire — browsing and posting are free.",
  },
  {
    question: "Can I cancel a hire?",
    answer:
      "Contact us as soon as possible if you need to cancel. Refund eligibility depends on whether the worker has already accepted and the circumstances of the cancellation.",
  },
];

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Orange accent bar */}
      <div className="h-1 bg-primary" />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
          <Link href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <HardHat className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold tracking-tight">RateRight</span>
          </Link>
          <Link href="/auth/login">
            <Button variant="ghost" size="sm">
              Sign in
            </Button>
          </Link>
        </div>
      </header>

      {/* Back link */}
      <div className="mx-auto max-w-screen-sm px-4 pt-6">
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      {/* FAQ Content */}
      <section className="px-4 pt-6 pb-12">
        <div className="mx-auto max-w-screen-sm">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
            Frequently Asked Questions
          </h1>
          <p className="mt-2 text-muted-foreground">
            Everything you need to know about RateRight.
          </p>

          <Accordion type="single" collapsible className="mt-8">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Bottom CTA */}
          <div className="mt-12 rounded-xl border bg-muted/50 p-6 text-center">
            <p className="font-semibold">Still have questions?</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Get in touch and we&apos;ll help you out.
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link href="/contractor/signup">
                <Button className="h-12 w-full gap-2 text-base font-semibold sm:w-auto">
                  Start hiring
                </Button>
              </Link>
              <Link href="/worker/signup">
                <Button
                  variant="outline"
                  className="h-12 w-full gap-2 text-base font-semibold sm:w-auto"
                >
                  Find work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t px-4 py-8">
        <div className="mx-auto max-w-screen-sm text-center text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
              <HardHat className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground">RateRight</span>
          </div>
          <p className="mt-2">
            The simplest way to hire construction workers in Australia.
          </p>
          <p className="mt-4">
            &copy; {new Date().getFullYear()} RateRight Pty Ltd. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
